import ActionBox from "@appComponents/ActionBox/ActionBox";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Space,
  Typography,
} from "antd";
import React, { useEffect, useState } from "react";
import DangerSVG from "@images/funds/danger.svg";
import {
  WithdrawalProcessSteps,
  useWithdrawalProcess,
} from "@funds/components/WithdrawProcess/useWithdrawalProcess";
import {
  MAIN_CURRENCY,
  MAIN_CURRENCY_LABEL,
  mainCurrencies,
} from "@bundles/consts";
import { IPrivateWallets } from "@funds/components/WithdrawAmount/IWithdrawAmount";
import { useFormik } from "formik";
import Big from "big.js";
import validationSchema from "./validationSchema";
import { IWalletDetails } from "@market/interfaces/IWallet";
import { IWithdrawFees } from "@funds/components/WithdrawForm/IWithdrawForm";
import BftNotice from "../../../BftNotice/BftNotice";
import { RouteComponentProps, withRouter } from "react-router-dom";
import NavigationFooter from "../../../NavigationFooter";
import CurrencyAvatar from "@funds/pages/MyFunds/tabs/FundsTab/parts/CurrencyAvatar";
import { avatarBgColor } from "@funds/pages/MyFunds/tabs/FundsTab/table-column";
import {
  getWalletFee,
  getWalletFeeObject,
} from "@funds/features/helpers/validation";
import { animateScrollTo } from "@features/utils/functions";
import { CRYPTO_PLACES } from "@appComponents/CurrencyElement/CurrencyElement";
import { useMediaQuery } from "react-responsive";
import PercentageBtns from "@funds/pages/MyFunds/pages/StakingPage/parts/shared/PercentageBtns";
import * as styles from "./WithdrawAmountStyle.module.scss";

type Props = {
  currency: string;
  cryptoWallets: IPrivateWallets[];
  userWallet: IWalletDetails;
  fees: IWithdrawFees[];
  exchangeRate: number | undefined;
} & RouteComponentProps<any>;

const WithdrawAmount = ({
  cryptoWallets,
  userWallet,
  fees,
  currency,
  exchangeRate,
}: Props) => {
  const { state, setState } = useWithdrawalProcess();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [selectedTag, setSelectedTag] = useState("");
  const isBftCurrency = currency === "bft";

  const renderCryptoWallets = () => {
    const wallets = cryptoWallets.map((el) => {
      const checked =
        String(el.id) ===
        String((state.selectedCryptoWallet as IPrivateWallets)?.id);

      return (
        <ActionBox
          data-cy={`${el.name}-wallet-btn`}
          key={el.id}
          headline={<p>{el.name || el.address}</p>}
          text={
            el.name ? (
              <>
                <Typography
                  style={{
                    fontWeight: "bold",
                    color: "#323232",
                    fontSize: "12px",
                  }}
                >
                  WALLET ADDRESS
                </Typography>
                {el.name && (
                  <Typography
                    style={{
                      fontSize: "12px",
                      color: "#323232",
                      marginTop: "5px",
                    }}
                  >
                    {el.address}
                  </Typography>
                )}
              </>
            ) : (
              <></>
            )
          }
          type="radio"
          radioKey={`crypto${el.id}`}
          radioAttr={{
            checked,
            value: el.id,
            onChange: async (e) => {
              setState({ ...state, selectedCryptoWallet: el });
              await formik.setFieldValue("selectedCryptoWallet", el);
            },
            name: "selectedWallet",
            "data-currency": el.currency,
            "data-usdtype": "tether",
          }}
          className={styles.actionBox}
        />
      );
    });

    const isMainCurrency = currency === MAIN_CURRENCY;

    const currencyLabel = isMainCurrency
      ? `(${MAIN_CURRENCY_LABEL.toUpperCase()} Withdrawal)`
      : null;

    return (
      <div style={{ marginTop: "40px" }}>
        <Typography.Title level={5}>
          Choose your wallet {currencyLabel}
        </Typography.Title>
        {wallets.length > 0 ? (
          wallets
        ) : (
          <Alert description="You don't have any wallets yet." type="info" />
        )}
      </div>
    );
  };

  const formik = useFormik({
    initialValues: {
      amount: state.amount,
      selectedCryptoWallet: state.selectedCryptoWallet,
      userWallet,
      fees,
      exchangeRate,
      agreement1: false,
    },
    onSubmit: (values) =>
      setState({
        ...state,
        amount: values.amount,
        step: WithdrawalProcessSteps.WITHDRAWAL_SUMMARY,
        percentage_value: selectedTag,
      }),
    validationSchema,
    enableReinitialize: true,
  });

  const handleAmountPercentage = async (amount: number) => {
    const result = Big(Big(userWallet.balance).times(Big(amount).div(Big(100))))
      .round(isMainCurrency ? 2 : CRYPTO_PLACES)
      .toString();
    setState({ ...state, amount: result.toString() });
    await formik.setFieldValue("amount", result.toString(), true);
    setSelectedTag(amount.toString());
  };

  useEffect(() => {
    if (!formik.isValid) {
      animateScrollTo(undefined, document.getElementById("withdrawform"), 20);
    }
  }, [formik.isSubmitting]);

  const isMainCurrency = [MAIN_CURRENCY, "usdt"].includes(currency ?? "");

  const currencyLabel = currency === "bfusd" ? "USDT" : currency?.toUpperCase();

  const renderTransactionFees = () => {
    const walletsCurrencies = cryptoWallets.map((el) => el.currency);
    const transactionFees = fees.filter((el) =>
      walletsCurrencies.includes(el.currency)
    );
    const minimumAmounts = transactionFees.map((el) => el.minimum_amount);
    const flatFees = transactionFees.map((el) => el.fees.flat_fee);
    const selectedMinimumAmount = Math.min(...minimumAmounts);

    const selectedWalletFeeObject = getWalletFeeObject(fees, currency);
    const selectedWalletFee = getWalletFee(selectedWalletFeeObject, 0);
    const defaultMinimumAmount =
      fees?.find((fee: any) => fee.currency === "default")?.minimum_amount || 0;

    const defaultMinimumAmountSetting = exchangeRate
      ? Big(defaultMinimumAmount).div(exchangeRate)
      : 0;

    const minimumAmountSetting =
      selectedWalletFeeObject && selectedWalletFeeObject.currency !== "default"
        ? selectedWalletFeeObject.minimum_amount
        : defaultMinimumAmountSetting;

    const minimumAmount = Big(minimumAmountSetting)
      .plus(selectedWalletFee)
      .round(CRYPTO_PLACES);

    const flatFee = Math.min(...flatFees);
    const cryptoTransactionFee = isFinite(flatFee)
      ? flatFee
      : selectedWalletFee;
    const cryptoMinimumAmount = isFinite(selectedMinimumAmount)
      ? Big(selectedMinimumAmount ?? 0)
      : minimumAmount;

    const currencyLabel =
      currency === "bfusd" ? "USDT" : currency?.toLocaleUpperCase();

    return (
      <div className={styles.transactionFees}>
        <div>
          <span>Estimated Transaction Fee:&nbsp;</span>
          {Big(cryptoTransactionFee).eq(0)
            ? "Blockchain fees"
            : `${Big(cryptoTransactionFee).round(
                isMainCurrency ? 2 : CRYPTO_PLACES
              )} ${currencyLabel}`}
        </div>

        <div>
          <span>Minimum Amount:&nbsp;</span>
          {`${cryptoMinimumAmount.round(
            isMainCurrency ? 2 : CRYPTO_PLACES
          )} ${currencyLabel}`}
        </div>
      </div>
    );
  };

  return (
    <Form className={styles.cryptoForm} onFinish={formik.handleSubmit}>
      <div className={styles.withdrawAmountWrapper}>
        {!isMobile && renderTransactionFees()}

        <Form.Item help={formik.errors.amount} label="Amount to withdraw">
          {isMobile && renderTransactionFees()}

          <Input
            data-cy="amount-field"
            suffix={
              <Space>
                <CurrencyAvatar
                  currency={currency}
                  background={
                    avatarBgColor[currency] ?? avatarBgColor["default"]
                  }
                />
                {currencyLabel}
              </Space>
            }
            placeholder="Enter amount"
            {...formik.getFieldProps("amount")}
            defaultValue={state.amount}
            autoComplete="off"
            onChange={async (e) => {
              let value = e.target.value;

              if (value.startsWith(".") || value.startsWith(",")) {
                value = `0${value}`;
              }

              if (!/^\d*\.?\d*$/.test(value)) {
                return;
              }

              await formik.setFieldValue("amount", value);
              setState({
                ...state,
                amount: value,
              });
            }}
          />
        </Form.Item>
      </div>

      <Row justify="space-between">
        <Col span={24}>
          <PercentageBtns
            handleAmountPercentage={handleAmountPercentage}
            selectedTag={selectedTag}
          />
        </Col>
      </Row>

      <Space direction="vertical" style={{ width: "100%", marginTop: "40px" }}>
        {isBftCurrency && <BftNotice />}

        {renderCryptoWallets()}
        <Button
          data-cy="add-crypto-wallet-btn"
          className={styles.addCryptoWallet}
          onClick={() =>
            setState({ ...state, step: WithdrawalProcessSteps.ADD_WALLET })
          }
        >
          Add crypto wallet +
        </Button>

        <Alert
          showIcon
          icon={<DangerSVG />}
          message="Custody of funds"
          description="All client funds are held solely by the Virtual Assets Broker in the British Virgin Islands. The BF Platform Operator in the Cayman Islands does not provide any custody services for client funds."
        />
      </Space>
      <div className={styles.agreementsWrapper}>
        <Form.Item
          required
          hasFeedback
          help={formik.touched.agreement1 ? formik.errors.agreement1 : false}
          className={styles.label}
        >
          <div className={styles.checkboxWraper}>
            <Checkbox
              data-cy="agreement1"
              {...formik.getFieldProps("agreement1")}
              checked={formik.values.agreement1}
              id="agreement1"
            >
              <Typography.Text>
                <label htmlFor="agreement1" className={styles.label}>
                  I confirm that my personal information, including name and
                  address, as currently shown in the Account section, is
                  accurate and up-to-date.
                </label>
              </Typography.Text>
            </Checkbox>
          </div>
        </Form.Item>
      </div>
      <NavigationFooter
        hideBackBtn={!mainCurrencies.includes(currency)}
        onCancel={() =>
          setState({ ...state, step: WithdrawalProcessSteps.SELECT_METHOD })
        }
        disabled={!formik.isValid || !formik.values.agreement1}
        onContinue={formik.submitForm}
      />
    </Form>
  );
};

export default withRouter(WithdrawAmount);
